import { zodResolver } from "@hookform/resolvers/zod"
import { Box } from "@mui/material"
import { FilterAltOutlined, PersonAddOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useParams, useSearch } from "@tanstack/react-router"
import type { ReactElement } from "react"
import { useEffect, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import ContactsCardList from "./ContactsCardList"
import { ContactSubscriptionDialog } from "./ContactSubscriptionDialog"

import { Button } from "@/components/common/Button"
import ListModeToggle from "@/components/common/ListModeToggle/ListModeToggle"
import CircularLoadingIndicator from "@/components/common/LoadingIndicators/CircularLoadingIndicator"
import EditContactLocationRolesDialog from "@/components/Contact/EditContactLocationRolesDialog"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import type { Contact, ContactCreateInput, ContactCreateSuccess } from "@/graphql/codegen/graphql"
import { CommentParentTableType, CompanyDepartment, ContactChannel, ContactType } from "@/graphql/codegen/graphql"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import type { ContactAddFormValues } from "@/schemas/contactFormSchema"
import { ContactAddFormSchema } from "@/schemas/contactFormSchema"
import { NotificationType } from "@/schemas/notification"
import ContactsFilterDrawer from "@/screens/Companies/components/filters/ContactsFilterDrawer"
import { COMPANY_EDIT_ROUTE } from "@/screens/Companies/constants"
import AddContactDialog from "@/screens/Companies/create/components/AddContactDialog"
import ContactsTable from "@/screens/Companies/edit/components/contacts/ContactsTable"
import EmptyContactsAlert from "@/screens/Companies/edit/components/contacts/EmptyContactsAlert"
import { useCompanyContactFilter } from "@/screens/Companies/edit/hooks/useCompanyContactFilter"
import { useCreateNewContactModal } from "@/screens/Companies/hooks/useCreateNewContact"
import { type ContactSearchFilters, useContactSearchStore } from "@/screens/Companies/stores/useContactSearchStore"
import { AddContactComment } from "@/screens/Contacts/edit/components/dialogs"
import { useGetContactMethods } from "@/screens/Contacts/edit/hooks/useGetContactMethods"
import { serializeContactLocationAddFormValues } from "@/screens/Contacts/utils/contactLocations"
import { useListModeStore } from "@/stores/useListModeStore"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { ListMode } from "@/types/common"

const ContactContainer = (): ReactElement => {
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false)
  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false)
  const [isLocationRolesDialogOpen, setIsLocationRolesDialogOpen] = useState(false)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [isSubscriptionsDialogOpen, setIsSubscriptionsDialogOpen] = useState(false)

  const { filterDrawerOpen, setFilterDrawerOpen } = useCompanyContactFilter()

  const navigate = useNavigate()
  const { companyId } = useParams({ from: COMPANY_EDIT_ROUTE })
  const currentSearch = useSearch({ from: "/app/companies/$companyId/edit/_layout/contacts" })
  const { batchCreateContactMethods } = useGetContactMethods()
  const { createContact, createContactLocation } = useCreateNewContactModal()
  const { listMode, setListMode } = useListModeStore()

  const data = useContactSearchStore(({ data }) => data)
  const fetching = useContactSearchStore(({ fetching }) => fetching)
  const executeSearch = useContactSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useContactSearchStore(({ loadMore }) => loadMore)
  const clearStore = useContactSearchStore(({ clearStore }) => clearStore)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const isEmptyContactsAlertDisplayed = data.length === 0 && !fetching && !Object.keys(currentSearch).length

  const searchFilters: ContactSearchFilters = useMemo(() => {
    const { isPrimaryContact, ...otherSearchParams } = currentSearch
    return {
      companyIds: [companyId],
      isPrimaryLocation: isPrimaryContact,
      ...otherSearchParams,
    }
  }, [companyId, currentSearch])

  useEffect(() => {
    navigate({
      search: {
        isActive: true,
      },
      to: `/app/companies/${companyId}/edit/contacts`,
      replace: true,
    })
  }, [companyId, navigate])

  const [throttledLoadMore] = useThrottle(() => {
    loadMore(queryClient, searchFilters)
  }, 200)

  useEffect(() => {
    executeSearch(queryClient, searchFilters)
  }, [executeSearch, searchFilters])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  const methods = useForm<ContactAddFormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      companyDepartment: "",
      title: "",
      physicalLocationId: "",
      phones: [{ number: "", type: "Text", isPreferred: true }],
      locationRoles: [{ locationId: "", roles: [] }],
      email: "",
    },
    resolver: zodResolver(ContactAddFormSchema),
    mode: "all",
  })

  const handleAddContact = () => {
    methods.reset()
    setIsAddContactDialogOpen(true)
  }

  const handleSubmit = async (data: ContactAddFormValues) => {
    const formattedContactDetails: ContactCreateInput = {
      companyId,
      firstName: data.firstName,
      lastName: data.lastName,
      companyDepartment: CompanyDepartment[data.companyDepartment as keyof typeof CompanyDepartment],
      title: data.title ?? "",
      physicalLocationId: data.physicalLocationId,
    }
    const { error, data: contactDetails } = await createContact({ input: formattedContactDetails })

    if (error?.message || contactDetails?.contact.create.__typename === "ContactCreateFailure") {
      notify({
        type: NotificationType.Error,
        message: error?.message || "Failed to create contact",
      })
    } else {
      const contactCreateSuccess = contactDetails?.contact.create as ContactCreateSuccess
      const { contactId } = contactCreateSuccess.createdContact
      if (!contactId) return

      const formattedContactMethods = [
        ...(data.phones ?? [])
          .filter(({ number }) => number)
          .sort((a) => (a.isPreferred ? -1 : 1))
          .map(({ number, type }) => ({
            contactId,
            contactType: ContactType[type as keyof typeof ContactType],
            value: number!,
            contactChannel: ContactChannel.Phone,
            phoneExt: "",
          })),
        ...(data.email
          ? [
              {
                contactId,
                value: data.email,
                contactType: ContactType.Work,
                contactChannel: ContactChannel.Email,
                phoneExt: "",
              },
            ]
          : []),
      ]

      await batchCreateContactMethods({
        input: {
          contactMethodCreateInputs: formattedContactMethods,
        },
      })

      const formContactLocations = serializeContactLocationAddFormValues(contactId, data.locationRoles)
      for (const contactLocation of formContactLocations) {
        await createContactLocation({ input: contactLocation })
      }
    }

    executeSearch(queryClient, searchFilters, true)
    setIsAddContactDialogOpen(false)
  }

  const handleContactClick = ({ contactId }: Contact) => {
    void navigate({ to: `/app/contact/${contactId}` })
  }

  const handleLocationRolesSubmit = () => {
    executeSearch(queryClient, searchFilters, true)
    setIsLocationRolesDialogOpen(false)
    setSelectedContact(null)
  }

  const handleSubscriptionsDialogSubmit = () => {
    executeSearch(queryClient, searchFilters, true)
    setIsCommentDialogOpen(false)
    setIsLocationRolesDialogOpen(false)
    setIsSubscriptionsDialogOpen(false)
    setSelectedContact(null)
  }

  const handleCloseDialogs = () => {
    setIsCommentDialogOpen(false)
    setIsLocationRolesDialogOpen(false)
    setIsSubscriptionsDialogOpen(false)
    setSelectedContact(null)
  }

  return (
    <Box className='flex h-full flex-col gap-4'>
      {isEmptyContactsAlertDisplayed ? (
        <EmptyContactsAlert handleAddContact={handleAddContact} />
      ) : (
        <>
          <TableHeaderActions currentSearch={currentSearch} placeholder='Search by Contact’s Name...'>
            <Button
              appearance='outlined'
              value='Filter'
              startIcon={<FilterAltOutlined />}
              onClick={() => setFilterDrawerOpen(true)}
              rootElementName='button'
            />
            <Box className='flex items-center justify-end'>
              <Button appearance='outlined' onClick={handleAddContact} className='mr-6'>
                <PersonAddOutlined />
                Add Contact
              </Button>
              <ListModeToggle listMode={listMode} onChange={(mode: ListMode) => setListMode(mode)} />
            </Box>
          </TableHeaderActions>

          <ContactsFilterDrawer
            companyId={companyId}
            showFilterDrawer={filterDrawerOpen}
            toggleDrawer={setFilterDrawerOpen}
          />

          {data.length === 0 && fetching && <CircularLoadingIndicator />}

          {listMode === ListMode.TABLE && (
            <ContactsTable
              fetching={fetching}
              data={data}
              setIsCommentDialogOpen={setIsCommentDialogOpen}
              setSelectedContact={setSelectedContact}
              onContactClick={handleContactClick}
              throttledLoadMore={throttledLoadMore}
            />
          )}

          {listMode === ListMode.CARDS && (
            <ContactsCardList
              fetching={fetching}
              data={data}
              setIsCommentDialogOpen={setIsCommentDialogOpen}
              setSelectedContact={setSelectedContact}
              setIsSubscriptionsDialogOpen={setIsSubscriptionsDialogOpen}
              setIsLocationRolesDialogOpen={setIsLocationRolesDialogOpen}
              onContactClick={handleContactClick}
              throttledLoadMore={throttledLoadMore}
            />
          )}
        </>
      )}

      <FormProvider {...methods}>
        <AddContactDialog
          open={isAddContactDialogOpen}
          onClose={() => {
            methods.reset()
            setIsAddContactDialogOpen(false)
          }}
          onSubmit={handleSubmit}
        />
      </FormProvider>
      {/* TODO: AK-2846 */}
      {selectedContact && (
        <>
          {isCommentDialogOpen && (
            <AddContactComment
              referenceId={selectedContact.contactId ?? ""}
              onClose={handleCloseDialogs}
              onSubmit={handleCloseDialogs}
              type={CommentParentTableType.Contact}
            />
          )}
          <ContactSubscriptionDialog
            open={isSubscriptionsDialogOpen}
            contactId={selectedContact.contactId ?? ""}
            name={`${selectedContact.firstName} ${selectedContact?.lastName}`}
            companyName={selectedContact.company?.name ?? undefined}
            onClose={handleCloseDialogs}
            onSubmit={handleSubscriptionsDialogSubmit}
          />
          <EditContactLocationRolesDialog
            open={isLocationRolesDialogOpen}
            contactId={selectedContact.contactId ?? ""}
            contactName={`${selectedContact.firstName} ${selectedContact.lastName}`}
            contactLocations={selectedContact.contactLocations}
            companyId={selectedContact.companyId ?? ""}
            companyName={selectedContact.company?.name ?? undefined}
            onClose={handleCloseDialogs}
            onSubmit={handleLocationRolesSubmit}
          />
        </>
      )}
    </Box>
  )
}

export default ContactContainer
